import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { apiFetch, getToken, API_BASE } from '../../services/apiClient';
import ReceivablesBoard from './ReceivablesBoard';
import { DaysPills, BillHistoryWindow, billStage } from '../../components/BillBits';
import { offerToSaveRecipients } from '../../components/ContactCheck';

// ─────────────────────────────────────────────────────────────────────────────
// Large Job Billing (Pat, Sep 23 2026) — Work Order Billing's twin for large jobs.
//
// A large job bills many times, so the card here is a pay application or invoice, not
// the job: a job with three pay apps shows three cards as they move. Draft → confirmed
// (a DIFFERENT person checks and sends it; an Executive may override) → sent → paid,
// then filed by year so the Paid column stays clear. Bills start on the job's Billing
// tile; this board is where they are watched and moved.
// ─────────────────────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'draft', label: 'To Bill',           tint: '#15803d', hint: 'Started on the job. Finish it and confirm.' },
  { key: 'ready', label: 'Needs Checking',    tint: '#c2410c', hint: 'Confirmed. Waiting on a second person.' },
  { key: 'sent',  label: 'Billed · Not Paid', tint: '#b45309', hint: 'Gone out. Waiting on the money.' },
  { key: 'paid',  label: 'Paid',              tint: '#166534', hint: 'Paid. File it away.' },
];
const money = n => (n == null || n === '' ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const shortDate = s => (s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 };
const btn = { padding: '7px 12px', borderRadius: 8, border: 'none', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' };
const inp = { border: '1px solid #d1d5db', borderRadius: 7, padding: '6px 8px', fontSize: 12.5, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };
const lab = { fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 3 };
function me() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null') || {}; } catch { return {}; } }
// An invoice's dates, as the shared day counters read them (Pat, Oct 7 2026).
export const invoiceFacts = b => ({ created_at: b.created_at, prepared_at: b.prepared_at, sent_at: b.sent_at, paid_at: b.paid_at,
  last_followup_at: b.last_followup_at, last_followup_kind: b.last_followup_kind, followup_count: b.followup_count });
const billName = b => (b.mode === 'sov' && b.application_no ? `Pay App #${b.application_no}${b.is_final ? ' · final' : ''}` : b.invoice_no ? `Invoice ${b.invoice_no}` : 'Draft invoice');

export default function LargeJobBilling() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [years, setYears] = useState([]);
  const [openYear, setOpenYear] = useState(null);
  const [filed, setFiled] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [showReceivables, setShowReceivables] = useState(false);
  const [history, setHistory] = useState(null);   // the card opened big with its history (Pat, Oct 7 2026)
  const session = me();
  const isExec = ['Executive', 'admin', 'Admin'].includes(session.role);

  const load = useCallback(() => {
    apiFetch('/invoices/board').then(r => setRows(Array.isArray(r) ? r : [])).catch(e => setErr(e?.error || 'Could not load these.'));
    apiFetch('/invoices/filed-years').then(y => setYears(Array.isArray(y) ? y : [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!openYear) { setFiled([]); return; }
    apiFetch(`/invoices/board?year=${openYear}`).then(r => setFiled(Array.isArray(r) ? r : [])).catch(() => setFiled([]));
  }, [openYear]);

  async function act(b, path, body) {
    setBusy(b.id); setErr('');
    try {
      await apiFetch(`/invoices/${b.id}${path}`, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
      await load();
    } catch (e) { setErr(e?.error || e?.message || 'That did not go through.'); }
    setBusy('');
  }

  const byCol = useMemo(() => {
    const out = Object.fromEntries(COLUMNS.map(c => [c.key, []]));
    for (const r of rows || []) (out[r.status] || out.draft).push(r);
    return out;
  }, [rows]);

  if (err && !rows) return <AdminLayout><div style={{ padding: 30, color: '#b91c1c' }}>{err}</div></AdminLayout>;
  if (!rows) return <AdminLayout><div style={{ padding: 30, color: '#6b7280' }}>Loading…</div></AdminLayout>;

  const waiting = byCol.ready.filter(b => b.prepared_by !== session.userId || isExec).length;
  const outstanding = byCol.sent.reduce((s, b) => s + (Number(b.total) || 0), 0);

  return (
    <AdminLayout>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        <h1 style={{ fontSize: 21, fontWeight: 800, margin: 0 }}>Large Job Billing</h1>
        <span style={{ fontSize: 12.5, color: '#9ca3af', flex: 1 }}>Pay applications and invoices on large jobs, through to paid and filed.</span>
        {waiting > 0 && (
          <span style={{ fontSize: 12, fontWeight: 800, color: '#c2410c', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 999, padding: '4px 12px' }}>
            {waiting} waiting on you to check
          </span>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 14 }}>
        Bills start on the job’s Billing tile. Whoever confirms one can’t be the one who sends it — someone else checks it first.
        {outstanding > 0 && <> &nbsp;·&nbsp; <b style={{ color: '#111' }}>{money(outstanding)}</b> billed and not paid.</>}
      </div>

      {err && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 700, marginBottom: 10 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(230px, 1fr))', gap: 12, alignItems: 'start', overflowX: 'auto' }}>
        {COLUMNS.map(c => (
          <div key={c.key} style={{ background: 'var(--surface-2, #f7f7f5)', borderRadius: 12, padding: 9, minHeight: 150 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '3px 4px' }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: c.tint }} />
              <span style={{ fontSize: 12.5, fontWeight: 800, color: '#111' }}>{c.label}</span>
              {c.key === 'sent' && (
                <button onClick={() => setShowReceivables(true)} title="Open this column four wide: 30 / 60 / 90 / 120 days, with follow-ups"
                  style={{ ...btn, padding: '2px 8px', fontSize: 11, background: '#b45309', color: '#fff', whiteSpace: 'nowrap' }}>
                  📊 Breakdown
                </button>
              )}
              <span style={{ fontSize: 11.5, color: '#9ca3af', marginLeft: 'auto' }}>{byCol[c.key].length}</span>
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', padding: '0 4px 9px' }}>{c.hint}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {byCol[c.key].map(b => (
                <BillCard key={b.id} b={b} column={c.key} busy={busy === b.id} session={session} isExec={isExec}
                  onOpen={() => navigate(`/admin/jobs/${b.project_id}?pillow=wo_billing`)} onHistory={() => setHistory({ b, column: c.key })}
                  onAct={(path, body) => act(b, path, body)} onChanged={load} onError={setErr} />
              ))}
              {byCol[c.key].length === 0 && <div style={{ fontSize: 11.5, color: '#c4c4c4', padding: '8px 6px' }}>Nothing here.</div>}
            </div>
          </div>
        ))}
      </div>

      {history && <BillHistoryWindow invoiceId={history.b.id} title={`${history.b.project_number} · ${billName(history.b)}`}
        sub={[history.b.project_name, history.b.bill_to_company, money(history.b.total), COLUMNS.find(c => c.key === history.column)?.label].filter(Boolean).join(' · ')}
        stage={billStage(history.column)} facts={invoiceFacts(history.b)}
        openHref={`/admin/jobs/${history.b.project_id}?pillow=wo_billing`} openLabel="Open it on the job ›" onNavigate={navigate} onClose={() => setHistory(null)} />}
      {showReceivables && <ReceivablesBoard source="large" onClose={() => { setShowReceivables(false); load(); }} />}

      {/* Filed, by the year it was paid and put away. Off the board, still readable. */}
      <div style={{ marginTop: 26 }}>
        <h2 style={{ fontSize: 15, fontWeight: 800, margin: '0 0 8px' }}>Filed</h2>
        {years.length === 0 ? (
          <div style={{ fontSize: 13, color: '#9ca3af' }}>Nothing filed yet.</div>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {years.map(y => (
              <button key={y.year} onClick={() => setOpenYear(openYear === y.year ? null : y.year)}
                style={{ ...btn, background: openYear === y.year ? '#111' : '#fff', color: openYear === y.year ? '#fff' : '#374151', border: '1px solid #e5e7eb' }}>
                {y.year} · {y.n}
              </button>
            ))}
          </div>
        )}
        {openYear && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
            {filed.map(b => (
              <div key={b.id} style={{ ...card, padding: '9px 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => navigate(`/admin/jobs/${b.project_id}?pillow=wo_billing`)} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer', flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: '#1e3a5f' }}>{b.project_number} · {billName(b)}</div>
                  <div style={{ fontSize: 11.5, color: '#4b5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.project_name}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{money(b.total)} · paid {shortDate(b.paid_at)} · filed {shortDate(b.filed_at)}</div>
                </button>
                <button onClick={() => act(b, '/unfile')} style={{ ...btn, background: '#fff', border: '1px solid #e5e7eb', color: '#6b7280', fontSize: 11.5 }}>↩ Back to Paid</button>
              </div>
            ))}
            {filed.length === 0 && <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Loading…</div>}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function BillCard({ b, column, busy, session, isExec, onOpen, onHistory, onAct, onChanged, onError }) {
  const [emailing, setEmailing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [sending, setSending] = useState(false);
  const canSend = b.prepared_by !== session.userId || isExec;

  // The PDF needs the session header, so it comes down as a blob rather than a plain link.
  function viewPdf() {
    const win = window.open('', '_blank');
    fetch(`${API_BASE}/invoices/${b.id}/pdf`, { headers: { 'x-worker-token': getToken() } }).then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => { const u = URL.createObjectURL(blob); if (win) win.location.href = u; else window.open(u, '_blank'); setTimeout(() => URL.revokeObjectURL(u), 60000); })
      .catch(() => { if (win) win.close(); onError('Could not draw the PDF.'); });
  }
  async function sendEmail() {
    if (sending || !draft?.to.trim()) return; setSending(true); onError('');
    try {
      await apiFetch(`/invoices/${b.id}/email`, { method: 'POST', body: JSON.stringify(draft) });
      offerToSaveRecipients({ to: draft.to, cc: draft.cc, clientName: b.bill_to_company || null });   // the bill-to in the Customer List? (Oct 6 2026)
      setEmailing(false); onChanged();
    } catch (e) { onError(e?.error || e?.message || 'That did not send.'); }
    setSending(false);
  }

  return (
    <div style={{ ...card, padding: '9px 10px', opacity: busy ? 0.5 : 1 }}>
      <button onClick={onHistory} title="Open it — everything that has happened on this bill" style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer', width: '100%' }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: '#1e3a5f' }}>{b.project_number} <span style={{ color: '#c2410c' }}>· {billName(b)}</span></div>
        <div style={{ fontSize: 11.5, color: '#4b5563', lineHeight: 1.35 }}>{b.project_name}</div>
        <DaysPills stage={billStage(column)} facts={invoiceFacts(b)} />
      </button>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: '#111', marginTop: 5 }}>
        {money(b.total)}
        {b.retainage_held != null && Number(b.retainage_held) > 0 && <span style={{ fontSize: 10.5, fontWeight: 600, color: '#9ca3af' }}> · {money(b.retainage_held)} retainage held</span>}
      </div>
      {b.pm_name && <div style={{ fontSize: 10.5, color: '#6b7280', marginTop: 2 }}>PM {b.pm_name}{b.period_to ? ` · through ${shortDate(b.period_to)}` : ''}</div>}
      {(b.prepared_by_name || b.sent_by_name || b.paid_by_name) && (
        <div style={{ fontSize: 10.5, color: '#6b7280', marginTop: 4 }}>
          {b.prepared_by_name && <>Confirmed by {b.prepared_by_name} {shortDate(b.prepared_at)}</>}
          {b.sent_by_name && <><br />Checked &amp; sent by {b.sent_by_name} {shortDate(b.sent_at)}{b.sent_to ? ` to ${b.sent_to}` : ''}</>}
          {b.paid_by_name && <><br />Paid {shortDate(b.paid_at)} ({b.paid_by_name})</>}
        </div>
      )}

      {column !== 'draft' && (
        <button onClick={viewPdf} style={{ ...btn, width: '100%', marginTop: 7, border: '1.5px solid #1d4ed8', background: '#eff6ff', color: '#1d4ed8' }}>🧾 View invoice</button>
      )}

      {column === 'draft' && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <button onClick={onOpen} style={{ ...btn, background: '#fff', border: '1.5px solid #15803d', color: '#15803d' }}>Open it on the job ›</button>
          <button onClick={() => onAct('/confirm')} title="Gives it the next invoice number; then someone else checks and sends it"
            style={{ ...btn, background: '#15803d', color: '#fff' }}>Confirm it</button>
        </div>
      )}

      {column === 'ready' && !emailing && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <button disabled={!canSend} onClick={() => onAct('/send')}
            title={canSend ? 'Check it and mark it sent' : 'You confirmed this one — it needs someone else'}
            style={{ ...btn, background: canSend ? '#065f46' : '#f3f4f6', color: canSend ? '#fff' : '#9ca3af' }}>
            {canSend ? '✓ Checked — send it' : 'Waiting on someone else'}
          </button>
          {canSend && (
            <button onClick={() => { setDraft({ to: b.bill_to_email || '', cc: 'billing@titanelectric.biz', subject: `Invoice ${b.invoice_no || ''} — ${b.project_number || ''} ${b.project_name || ''}`.trim(), message: '' }); setEmailing(true); }}
              style={{ ...btn, background: '#fff', border: '1.5px solid #065f46', color: '#065f46' }}>✉️ Email it (PDF attached)</button>
          )}
          <button onClick={() => onAct('/reopen')} style={{ background: 'none', border: 'none', padding: '4px 0 0', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#6b7280' }}>↩ Back to draft</button>
        </div>
      )}
      {column === 'ready' && emailing && draft && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label><span style={lab}>To</span><input style={inp} value={draft.to} onChange={e => setDraft(d => ({ ...d, to: e.target.value }))} placeholder="billing@customer.com" /></label>
          <label><span style={lab}>Cc</span><input style={inp} value={draft.cc} onChange={e => setDraft(d => ({ ...d, cc: e.target.value }))} /></label>
          <label><span style={lab}>Subject</span><input style={inp} value={draft.subject} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))} /></label>
          <label><span style={lab}>Message</span><textarea style={{ ...inp, resize: 'vertical' }} rows={4} value={draft.message} onChange={e => setDraft(d => ({ ...d, message: e.target.value }))} placeholder="Optional note above the invoice." /></label>
          <div style={{ display: 'flex', gap: 5 }}>
            <button onClick={sendEmail} disabled={sending || !draft.to.trim()} style={{ ...btn, flex: 1, background: draft.to.trim() ? '#065f46' : '#e5e7eb', color: draft.to.trim() ? '#fff' : '#9ca3af' }}>{sending ? 'Sending…' : 'Send it'}</button>
            <button onClick={() => setEmailing(false)} style={{ ...btn, background: '#fff', border: '1px solid #d1d5db', color: '#6b7280' }}>Cancel</button>
          </div>
        </div>
      )}

      {column === 'sent' && (
        <button onClick={() => onAct('/paid')} style={{ ...btn, width: '100%', marginTop: 8, background: '#166534', color: '#fff' }}>Mark paid</button>
      )}

      {column === 'paid' && (
        <button onClick={() => onAct('/file')} style={{ ...btn, width: '100%', marginTop: 8, background: '#1e3a5f', color: '#fff' }}>File it</button>
      )}
    </div>
  );
}
